import { isNil } from 'lodash-es';
import {
  required,
  minLength,
  maxLength,
  minValue,
  maxValue,
  numeric,
  email,
  regex,
} from '@regle/rules';
import type { RegleRuleDefinition } from '@regle/core';
import { formatMessage, validationMessages } from '../../messages';
import { isEmptyValue } from '../forms/useItpSelectProvider';

export type ValidationErrors = Record<string, string>;

interface ParsedRule {
  name: string;
  params: any[];
}

function callRule(
  rule: RegleRuleDefinition<any, any, any>,
  value: any,
): boolean {
  const result = rule.exec(value) as boolean | { $valid?: boolean };
  return typeof result === 'boolean' ? result : !!result?.$valid;
}

function getRegleRule(
  name: string,
  params: any[],
): RegleRuleDefinition<any, any, any> | null {
  switch (name) {
    case 'required':
      return required;
    case 'email':
      return email;
    case 'numeric':
      return numeric;

    case 'min':
    case 'minLength':
      return minLength(Number(params[0]));

    case 'max':
    case 'maxLength':
      return maxLength(Number(params[0]));

    case 'minValue':
    case 'min_value':
      return minValue(Number(params[0]));

    case 'maxValue':
    case 'max_value':
      return maxValue(Number(params[0]));

    case 'regex':
      return regex(new RegExp(params[0]));

    default:
      return null;
  }
}

const customValidators: Record<string, (value: any, params: any[]) => boolean> =
  {
    in: (value, params) => {
      const options = params[0].split('|');
      return options.includes(String(value));
    },
    double: (value) => {
      if (isNil(value) || value === '') {
        return true;
      }
      return /^-?\d*(\.\d+)?$/.test(String(value));
    },
  };

export function parseValidationRules(
  validator?: string | string[],
  isRequired?: boolean,
): ParsedRule[] {
  const rules: ParsedRule[] = [];

  if (isRequired) {
    rules.push({ name: 'required', params: [] });
  }

  if (!validator) {
    return rules;
  }

  const ruleStrings = Array.isArray(validator)
    ? validator
    : validator.split(';');

  for (const ruleStr of ruleStrings) {
    if (!ruleStr) {
      continue;
    }
    const [name, args] = ruleStr.split(':');
    const params = args ? args.split(',') : [];

    if (name === 'required' && isRequired) {
      continue;
    }

    rules.push({ name, params });
  }

  return rules;
}

export function validateValue(
  cellValue: any,
  rules: ParsedRule[],
  fieldLabel: string = validationMessages.field,
): string | null {
  const value = isEmptyValue(cellValue) ? null : cellValue;

  for (const rule of rules) {
    let isValid = true;

    if (rule.name !== 'required' && (isNil(value) || value === '')) {
      continue;
    }

    const regleRule = getRegleRule(rule.name, rule.params);
    if (regleRule) {
      isValid = callRule(regleRule, value);
    } else if (customValidators[rule.name]) {
      isValid = customValidators[rule.name](value, rule.params);
    } else {
      console.warn(`[DataTable] Unknown validator rule: ${rule.name}`);
    }

    if (!isValid) {
      return getErrorMessage(rule.name, fieldLabel, rule.params);
    }
  }
  return null;
}

function getErrorMessage(
  ruleName: string,
  label: string,
  params: any[],
): string {
  const m = validationMessages;
  const format = (message: string, n: any = '') =>
    formatMessage(message, { label, n });

  switch (ruleName) {
    case 'required':
      return format(m.required);
    case 'min':
    case 'minLength':
      return format(m.minLength, params[0]);
    case 'max':
    case 'maxLength':
      return format(m.maxLength, params[0]);
    case 'minValue':
    case 'min_value':
      return format(m.minValue, params[0]);
    case 'maxValue':
    case 'max_value':
      return format(m.maxValue, params[0]);
    case 'numeric':
    case 'double':
      return format(m.number);
    case 'email':
      return format(m.email);
    case 'in':
      return format(m.oneOf, params[0].split('|').join(', '));
    case 'regex':
      return format(m.format);
    default:
      return format(m.invalid);
  }
}
